import { CSSProperties, useState } from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import cx from 'classnames'

type Props = {
  show?: boolean
  visible?: boolean
  overlay?: React.ReactNode
  placement?: string
  trigger?: string | string[]
  overlayClassName?: string
  overlayStyle?: CSSProperties
  mouseEnterDelay?: number
  mouseLeaveDelay?: number
  destroyTooltipOnHide?: boolean
  getTooltipContainer?: () => HTMLElement | null
  align?: unknown
  children?: React.ReactNode
}

const placementMap: Record<
  string,
  {
    side: 'top' | 'bottom' | 'left' | 'right'
    align: 'start' | 'center' | 'end'
  }
> = {
  top: { side: 'top', align: 'center' },
  bottom: { side: 'bottom', align: 'center' },
  left: { side: 'left', align: 'center' },
  right: { side: 'right', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
  leftTop: { side: 'left', align: 'start' },
  leftBottom: { side: 'left', align: 'end' },
  rightTop: { side: 'right', align: 'start' },
  rightBottom: { side: 'right', align: 'end' },
}

const Tooltip: React.FC<Props> = ({
  show: showProp = true,
  visible,
  placement = 'top',
  children,
  overlay,
  overlayClassName,
  overlayStyle,
  trigger,
  mouseEnterDelay,
  mouseLeaveDelay: _mouseLeaveDelay,
  destroyTooltipOnHide: _destroyTooltipOnHide,
  getTooltipContainer,
  align: _align,
}) => {
  const isClickTrigger =
    trigger === 'click' || (Array.isArray(trigger) && trigger.includes('click'))
  const [clickOpen, setClickOpen] = useState(false)

  const show = visible !== undefined ? visible : showProp

  if (!show) {
    return <>{children}</>
  }

  const { side, align } = placementMap[placement] ?? {
    side: 'top',
    align: 'center',
  }
  const delayDuration =
    mouseEnterDelay != null ? Math.round(mouseEnterDelay * 1000) : undefined
  const portalContainer = getTooltipContainer
    ? getTooltipContainer()
    : undefined

  return (
    <RadixTooltip.Root
      {...(isClickTrigger
        ? { open: clickOpen, onOpenChange: setClickOpen }
        : {})}
      {...(delayDuration != null ? { delayDuration } : {})}
    >
      <RadixTooltip.Trigger asChild>
        <span
          className="inline-flex"
          onClick={isClickTrigger ? () => setClickOpen((v) => !v) : undefined}
        >
          {children}
        </span>
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal container={portalContainer ?? undefined}>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={4}
          className={cx(
            'bg-coolGray-900 text-white px-2 py-1 rounded text-sm z-[1070]',
            overlayClassName
          )}
          style={overlayStyle}
        >
          {overlay}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}

export default Tooltip
